import { useEffect, useState } from "react";
import { getSettings, Setting } from "@/Api/settings";
import { DURATION_VALUE_TYPES, DATE_RANGE_VALUE_TYPE } from "@/constants/settings";

/**
 * Cache global das configurações do sistema.
 *
 * A API é chamada uma única vez por sessão do app (GET /settings) e o
 * resultado é compartilhado por todas as telas — o mesmo padrão usado em
 * usePermissions. Quem editar uma configuração deve chamar refreshSettings().
 */
let globalSettings: Setting[] | null = null;
let globalLoading = false;
let subscribers: (() => void)[] = [];

const notifySubscribers = () => {
  subscribers.forEach((callback) => callback());
};

const subscribe = (callback: () => void) => {
  subscribers.push(callback);
  return () => {
    subscribers = subscribers.filter((sub) => sub !== callback);
  };
};

const fetchSettings = async () => {
  try {
    globalLoading = true;
    notifySubscribers();

    const { data, ok } = await getSettings();

    globalSettings = ok && data?.response ? (data.response as Setting[]) : [];
  } catch (error) {
    globalSettings = [];
  } finally {
    globalLoading = false;
    notifySubscribers();
  }
};

export const getSetting = (key: string): Setting | null =>
  globalSettings?.find((setting) => setting.key === key) ?? null;

/**
 * Valor bruto de uma chave, considerando apenas configurações ativas.
 */
export const getSettingValue = (key: string): string | null => {
  const setting = getSetting(key);

  if (!setting || !setting.is_active) return null;

  return setting.value && setting.value !== "" ? setting.value : null;
};

const DURATION_IN_MS: Record<string, number> = {
  minutes: 60 * 1000,
  hours: 60 * 60 * 1000,
  days: 24 * 60 * 60 * 1000,
  weeks: 7 * 24 * 60 * 60 * 1000,
};

/**
 * Converte uma chave de prazo no instante limite a partir de uma data base.
 *
 * Retorna null quando não há prazo configurado — nesse caso não existe limite
 * e a ação segue liberada, espelhando a regra aplicada no backend.
 */
export const getSettingDeadline = (
  key: string,
  from: string | Date
): Date | null => {
  const setting = getSetting(key);
  const value = getSettingValue(key);

  if (!setting || value === null) return null;

  const base = from instanceof Date ? from : new Date(from);

  if (Number.isNaN(base.getTime())) return null;

  if ((DURATION_VALUE_TYPES as readonly string[]).includes(setting.value_type)) {
    const amount = Number(value);

    if (!Number.isFinite(amount) || amount <= 0) return null;

    const deadline = new Date(base.getTime());

    if (setting.value_type === "months") {
      deadline.setMonth(deadline.getMonth() + amount);
      return deadline;
    }

    if (setting.value_type === "years") {
      deadline.setFullYear(deadline.getFullYear() + amount);
      return deadline;
    }

    return new Date(base.getTime() + amount * DURATION_IN_MS[setting.value_type]);
  }

  if (setting.value_type === "date" || setting.value_type === "datetime") {
    const deadline = new Date(value);
    return Number.isNaN(deadline.getTime()) ? null : deadline;
  }

  return null;
};

const DURATION_LABELS: Record<string, [string, string]> = {
  minutes: ["minuto", "minutos"],
  hours: ["hora", "horas"],
  days: ["dia", "dias"],
  weeks: ["semana", "semanas"],
  months: ["mês", "meses"],
  years: ["ano", "anos"],
};

/**
 * Rótulo legível do prazo configurado (ex.: "24 horas"), para compor mensagens.
 * Retorna null quando a chave não é de duração ou não tem prazo configurado.
 */
export const describeSettingDuration = (key: string): string | null => {
  const setting = getSetting(key);
  const value = getSettingValue(key);

  if (!setting || value === null) return null;

  const label = DURATION_LABELS[setting.value_type];
  const amount = Number(value);

  if (!label || !Number.isFinite(amount) || amount <= 0) return null;

  return `${amount} ${amount === 1 ? label[0] : label[1]}`;
};

export interface SettingDateRange {
  start: Date | null;
  end: Date | null;
}

const parseRangeDate = (value: unknown, endOfDay: boolean): Date | null => {
  if (typeof value !== "string" || value === "") return null;

  // Datas vêm como "YYYY-MM-DD"; monta no fuso local para evitar deslocamento
  const [year, month, day] = value.split("-").map(Number);

  if (!year || !month || !day) return null;

  return endOfDay
    ? new Date(year, month - 1, day, 23, 59, 59, 999)
    : new Date(year, month - 1, day, 0, 0, 0, 0);
};

/**
 * Período (intervalo de datas) configurado para uma chave do tipo date_range.
 * Retorna null quando não há período configurado — sem restrição, portanto.
 */
export const getSettingDateRange = (key: string): SettingDateRange | null => {
  const setting = getSetting(key);
  const value = getSettingValue(key);

  if (!setting || value === null || setting.value_type !== DATE_RANGE_VALUE_TYPE) {
    return null;
  }

  try {
    const parsed = JSON.parse(value);
    const start = parseRangeDate(parsed?.start_date, false);
    const end = parseRangeDate(parsed?.end_date, true);

    return start || end ? { start, end } : null;
  } catch {
    return null;
  }
};

/**
 * Indica se HOJE está dentro do período configurado.
 * Sem período configurado, retorna true.
 */
export const isWithinSettingDateRange = (key: string): boolean => {
  const range = getSettingDateRange(key);

  if (!range) return true;

  const now = Date.now();

  if (range.start && now < range.start.getTime()) return false;
  if (range.end && now > range.end.getTime()) return false;

  return true;
};

/**
 * Indica se a data base ainda está dentro do prazo da configuração.
 * Sem prazo configurado, retorna true.
 */
export const isWithinSettingDeadline = (
  key: string,
  from: string | Date
): boolean => {
  const deadline = getSettingDeadline(key, from);

  if (!deadline) return true;

  return Date.now() <= deadline.getTime();
};

export const useSettings = () => {
  const [, setTrigger] = useState(0);

  useEffect(() => {
    const authData = JSON.parse(localStorage.getItem("auth") || "{}");

    if (authData.authorize && authData.token && !globalSettings && !globalLoading) {
      fetchSettings();
    }

    const unsubscribe = subscribe(() => {
      setTrigger((prev) => prev + 1);
    });

    return unsubscribe;
  }, []);

  return {
    settings: globalSettings ?? [],
    loading: globalLoading,
    getSetting,
    getSettingValue,
    getSettingDeadline,
    isWithinSettingDeadline,
    describeSettingDuration,
    getSettingDateRange,
    isWithinSettingDateRange,
  };
};

export const refreshSettings = () => {
  globalSettings = null;
  return fetchSettings();
};

export const clearSettings = () => {
  globalSettings = null;
  globalLoading = false;
  notifySubscribers();
};
